// src/components/Layout.js

import React, { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Header from './Header';
import Footer from './Footer';
import { fetchUserData, selectUserStatus } from '../features/userSlice';
import { fetchUnits } from '../features/unitsSlice';

/**
 * Layout component that wraps all authenticated routes.
 * Centralizes data fetching for user and units data to prevent
 * duplicate API calls across child components.
 * 
 * Data fetching strategy:
 * 1. The thunks themselves have `condition` options that prevent duplicate requests
 * 2. Units are fetched after user data is loaded to ensure proper default unit selection
 * 3. This pattern is resilient to React StrictMode double-effect invocation
 */
const Layout = () => {
    const dispatch = useDispatch();
    const userStatus = useSelector(selectUserStatus);
    const unitsStatus = useSelector((state) => state.units.status);

    // Fetch user data when Layout mounts
    // The thunk's `condition` option handles deduplication automatically
    useEffect(() => {
        dispatch(fetchUserData());
    }, [dispatch]);

    // Fetch units after user data is successfully loaded
    // This ensures we have user data for proper default unit selection
    // The thunk's `condition` option handles deduplication automatically
    useEffect(() => {
        if (userStatus === 'succeeded') {
            dispatch(fetchUnits());
        }
    }, [dispatch, userStatus]);

    return (
        <div className="d-flex flex-column min-vh-100">
            <Header />
            <main className="flex-grow-1">
                <Outlet />
            </main>
            <Footer />
        </div>
    );
};

export default Layout;
